// /agency/$name — agency profile.
// Same virtual-entity pattern as /vendor/$name — keyed by raw agency_raw_name
// until canonicalization to the agencies table is wired.

import { apiFetch } from '@/lib/api';
import { compactUsd, ts } from '@/lib/format';
import { EmptyState, ObjectHeader, TrackButton } from '@/primitives';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_authed/agency/$name')({
  component: AgencyPage,
});

interface AgencyDetail {
  id: string | null;
  name: string;
  jurisdiction: string | null;
  abbreviation: string | null;
  tender_count: number;
  award_count: number;
  contract_count: number;
  total_spend: { value: number; currency: string } | null;
  top_vendors: Array<{
    vendor_id: string | null;
    name: string;
    spend: { value: number; currency: string };
    count: number;
  }>;
}

function AgencyPage() {
  const { name } = Route.useParams();

  const detail = useQuery({
    queryKey: ['agency', name],
    queryFn: () => apiFetch<AgencyDetail>(`/v1/agencies/${encodeURIComponent(name)}`),
  });

  if (detail.isLoading) {
    return (
      <div style={{ flex: 1, position: 'relative' }}>
        <div className="progress" />
        <div className="meta mono" style={{ padding: 24 }}>
          resolving agency…
        </div>
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <EmptyState
        glyph="AGY"
        reason="Agency not found"
        detail={`No records under "${name}". Try /agencies for the full list.`}
      />
    );
  }

  const a = detail.data;
  const value = a.total_spend?.value ?? 0;

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}
    >
      <ObjectHeader
        type="AGY"
        id={a.id?.slice(0, 8) ?? `PEND-${name.slice(0, 6).toUpperCase()}`}
        title={a.name}
        sub={`${a.tender_count} tenders · ${a.award_count} awards · ${a.contract_count} contracts`}
        pills={[
          { tone: 'primary', label: compactUsd(value) },
          { tone: 'amber', label: 'PENDING ENTITY RESOLUTION' },
        ]}
        metrics={[
          { k: 'TOTAL SPEND', v: compactUsd(value) },
          { k: 'TENDERS', v: ts(a.tender_count) },
          { k: 'AWARDS', v: ts(a.award_count) },
          { k: 'CONTRACTS', v: ts(a.contract_count) },
        ]}
        actions={a.id ? <TrackButton entityType="agency" entityId={a.id} /> : undefined}
      />

      <div className="pane" style={{ margin: 16 }}>
        <div className="pane-h">
          <span>TOP VENDORS (BY SPEND)</span>
        </div>
        <div className="pane-body">
          {a.top_vendors.length === 0 ? (
            <div className="empty">
              <div className="empty-reason">No vendor spend recorded</div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>VENDOR</th>
                  <th className="r">SPEND</th>
                  <th className="r">COUNT</th>
                </tr>
              </thead>
              <tbody>
                {a.top_vendors.map((v) => (
                  <tr
                    key={v.name}
                    onClick={() => (window.location.href = `/vendor/${encodeURIComponent(v.name)}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{v.name}</td>
                    <td className="r mn">{compactUsd(v.spend.value)}</td>
                    <td className="r mn">{ts(v.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
